import React from "react";
import { NavLink } from "react-router-dom";
import { useLang } from "../../context/LanguageContext";
import {
  LayoutDashboard, Building2, Home, Users, UserCheck,
  Banknote, Receipt, FileBarChart, MessageSquare, Settings, X, Sprout,
} from "lucide-react";

const navItems = [
  { to: "/app", end: true, key: "dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/app/clfs", key: "clfs", icon: Building2, testId: "nav-clfs" },
  { to: "/app/vos", key: "vos", icon: Home, testId: "nav-vos" },
  { to: "/app/shgs", key: "shgs", icon: Users, testId: "nav-shgs" },
  { to: "/app/members", key: "members", icon: UserCheck, testId: "nav-members" },
  { to: "/app/loans", key: "loans", icon: Banknote, testId: "nav-loans" },
  { to: "/app/repayments", key: "repayments", icon: Receipt, testId: "nav-repayments" },
  { to: "/app/reports", key: "reports", icon: FileBarChart, testId: "nav-reports" },
  { to: "/app/whatsapp", key: "whatsapp", icon: MessageSquare, testId: "nav-whatsapp" },
  { to: "/app/settings", key: "settings", icon: Settings, testId: "nav-settings" },
];

export const Sidebar = ({ open, onClose }) => {
  const { t } = useLang();

  const content = (
    <div className="flex h-full flex-col bg-[#0F4C3A] text-emerald-50">
      <div className="flex items-center justify-between px-5 py-5 border-b border-emerald-50/10">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#D95D39]/90 text-white shadow-md">
            <Sprout className="h-5 w-5" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-base font-bold tracking-tight" data-testid="sidebar-brand-name">{t.appName}</div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-200/70">
              {t.appTagline}
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1.5 text-emerald-100/80 hover:bg-emerald-50/10 lg:hidden"
          data-testid="sidebar-close-btn"
          aria-label="Close sidebar"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
        {navItems.map(({ to, end, key, icon: Icon, testId }) => (
          <NavLink
            key={key}
            to={to}
            end={end}
            data-testid={testId}
            onClick={() => onClose && onClose()}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-emerald-50/15 text-white"
                  : "text-emerald-100/80 hover:bg-emerald-50/8 hover:text-white"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon className={`h-4.5 w-4.5 shrink-0 transition-colors ${isActive ? "text-[#F8B195]" : "text-emerald-200/70 group-hover:text-emerald-100"}`} strokeWidth={2} />
                <span className="truncate">{t.nav[key]}</span>
                {isActive && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[#D95D39]" />}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-emerald-50/10 px-5 py-4 text-[11px] leading-relaxed text-emerald-100/60">
        <div className="font-semibold text-emerald-50/80" data-testid="sidebar-brand-slogan">{t.appSlogan}</div>
        <div className="mt-1" data-testid="sidebar-brand-org">{t.appOrg}</div>
        <div data-testid="sidebar-brand-developer">{t.appDeveloper}</div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop */}
      <aside className="hidden lg:flex lg:w-64 lg:shrink-0 lg:flex-col lg:border-r lg:border-emerald-950/10">
        {content}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden" data-testid="sidebar-mobile-drawer">
          <div
            className="absolute inset-0 bg-emerald-950/60 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden
          />
          <aside className="absolute left-0 top-0 h-full w-72 shadow-2xl">
            {content}
          </aside>
        </div>
      )}
    </>
  );
};
